import React, { useState, useEffect } from 'react';
import CodeEditor from './CodeEditor';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CSSPlayground = () => {
  const [selectedExample, setSelectedExample] = useState('animations');
  const [cssCode, setCssCode] = useState('');
  const [htmlCode, setHtmlCode] = useState('');

  const examples = [
    {
      id: 'animations',
      name: 'CSS Animations',
      icon: 'Zap',
      css: `.animated-box {
  width: 100px;
  height: 100px;
  background: linear-gradient(45deg, #3b82f6, #8b5cf6);
  border-radius: 12px;
  margin: 20px auto;
  animation: bounce 2s infinite;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-30px);
  }
  60% {
    transform: translateY(-15px);
  }
}

.pulse-button {
  padding: 12px 24px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  animation: pulse 2s infinite;
  margin: 10px;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}`,
      html: `<div class="animated-box"></div>
<button class="pulse-button">Pulse Button</button>`
    },
    {
      id: 'flexbox',
      name: 'Flexbox Layout',
      icon: 'Layout',
      css: `.flex-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  margin: 20px 0;
  min-height: 120px;
}

.flex-item {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  flex: 1;
  margin: 0 10px;
  transition: transform 0.3s ease;
}

.flex-item:hover {
  transform: translateY(-5px);
}

.flex-item:first-child {
  margin-left: 0;
}

.flex-item:last-child {
  margin-right: 0;
}

.centered-content {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  background: #e2e8f0;
  border-radius: 8px;
  margin: 10px 0;
}`,
      html: `<div class="flex-container">
  <div class="flex-item">Item 1</div>
  <div class="flex-item">Item 2</div>
  <div class="flex-item">Item 3</div>
</div>
<div class="centered-content">
  <p>Perfectly Centered Content</p>
</div>`
    },
    {
      id: 'grid',
      name: 'CSS Grid',
      icon: 'Grid3x3',
      css: `.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  grid-gap: 20px;
  padding: 20px;
  background: #f1f5f9;
  border-radius: 12px;
  margin: 20px 0;
}

.grid-item {
  background: linear-gradient(45deg, #ff6b6b, #feca57);
  color: white;
  padding: 30px 20px;
  border-radius: 8px;
  text-align: center;
  font-weight: bold;
  transition: all 0.3s ease;
}

.grid-item:hover {
  transform: scale(1.05);
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
}

.grid-item:nth-child(1) {
  grid-column: span 2;
  background: linear-gradient(45deg, #667eea, #764ba2);
}

.grid-item:nth-child(4) {
  grid-row: span 2;
  background: linear-gradient(45deg, #f093fb, #f5576c);
}`,
      html: `<div class="grid-container">
  <div class="grid-item">Header (spans 2 columns)</div>
  <div class="grid-item">Item 2</div>
  <div class="grid-item">Item 3</div>
  <div class="grid-item">Tall Item (spans 2 rows)</div>
  <div class="grid-item">Item 5</div>
  <div class="grid-item">Item 6</div>
</div>`
    },
    {
      id: 'transforms',
      name: '3D Transforms',
      icon: 'Box',
      css: `.transform-container {
  perspective: 1000px;
  padding: 40px;
  text-align: center;
}

.card-3d {
  width: 200px;
  height: 120px;
  margin: 20px auto;
  position: relative;
  transform-style: preserve-3d;
  transition: transform 0.6s;
  cursor: pointer;
}

.card-3d:hover {
  transform: rotateY(180deg);
}

.card-face {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
}

.card-front {
  background: linear-gradient(45deg, #3b82f6, #1d4ed8);
}

.card-back {
  background: linear-gradient(45deg, #10b981, #059669);
  transform: rotateY(180deg);
}

.rotating-cube {
  width: 80px;
  height: 80px;
  margin: 20px auto;
  animation: rotate3d 4s infinite linear;
  background: linear-gradient(45deg, #8b5cf6, #7c3aed);
  border-radius: 8px;
}

@keyframes rotate3d {
  0% { transform: rotateX(0deg) rotateY(0deg); }
  25% { transform: rotateX(90deg) rotateY(0deg); }
  50% { transform: rotateX(90deg) rotateY(90deg); }
  75% { transform: rotateX(0deg) rotateY(90deg); }
  100% { transform: rotateX(0deg) rotateY(0deg); }
}`,
      html: `<div class="transform-container">
  <div class="card-3d">
    <div class="card-face card-front">
      Hover Me!
    </div>
    <div class="card-face card-back">
      Flipped!
    </div>
  </div>
  <div class="rotating-cube"></div>
</div>`
    }
  ];

  React.useEffect(() => {
    const example = examples?.find(ex => ex?.id === selectedExample);
    if (example) {
      setCssCode(example?.css);
      setHtmlCode(example?.html);
    }
  }, [selectedExample]);

  return (
    <div className="space-y-6">
      {/* Example Selector */}
      <div className="flex flex-wrap gap-2">
        {examples?.map((example) => (
          <Button
            key={example?.id}
            variant={selectedExample === example?.id ? 'default' : 'outline'}
            size="sm"
            iconName={example?.icon}
            iconPosition="left"
            onClick={() => setSelectedExample(example?.id)}
            className="transition-all duration-300"
          >
            {example?.name}
          </Button>
        ))}
      </div>
      {/* Code Editors and Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Code Editors */}
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center space-x-2">
              <Icon name="Palette" size={20} className="text-accent" />
              <span>CSS Code</span>
            </h3>
            <CodeEditor
              title="styles.css"
              language="css"
              initialCode={cssCode}
              onCodeChange={setCssCode}
              showOutput={false}
            />
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center space-x-2">
              <Icon name="Code" size={20} className="text-accent" />
              <span>HTML Structure</span>
            </h3>
            <CodeEditor
              title="index.html"
              language="html"
              initialCode={htmlCode}
              onCodeChange={setHtmlCode}
              showOutput={false}
            />
          </div>
        </div>

        {/* Live Preview */}
        <div>
          <h3 className="text-lg font-semibold mb-3 flex items-center space-x-2">
            <Icon name="Eye" size={20} className="text-accent" />
            <span>Live Preview</span>
          </h3>
          
          <div className="rounded-lg border border-border bg-white min-h-96 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-gray-50">
              <span className="text-sm font-medium">Preview</span>
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
            </div>
            
            <div className="p-6">
              <style dangerouslySetInnerHTML={{ __html: cssCode }} />
              <div dangerouslySetInnerHTML={{ __html: htmlCode }} />
            </div>
          </div>
        </div>
      </div>
      {/* CSS Tips */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="font-medium mb-2 flex items-center space-x-2">
            <Icon name="Lightbulb" size={16} className="text-warning" />
            <span>CSS Best Practices</span>
          </h4>
          <ul className="text-sm text-text-secondary space-y-1">
            <li>• Use CSS custom properties (variables) for consistent theming</li>
            <li>• Prefer flexbox and grid for layout over floats</li>
            <li>• Use transform and opacity for smooth animations</li>
            <li>• Apply box-sizing: border-box for predictable sizing</li>
            <li>• Use relative units (rem, em, %) for responsive design</li>
          </ul>
        </div>

        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="font-medium mb-2 flex items-center space-x-2">
            <Icon name="Zap" size={16} className="text-accent" />
            <span>Performance Tips</span>
          </h4>
          <ul className="text-sm text-text-secondary space-y-1">
            <li>• Use will-change property for elements that will animate</li>
            <li>• Avoid animating layout properties (width, height, margin)</li>
            <li>• Use transform3d() to trigger hardware acceleration</li>
            <li>• Minimize repaints with efficient selectors</li>
            <li>• Use CSS containment for better rendering performance</li>
          </ul>
        </div>
      </div>
      {/* Interactive Elements Demo */}
      <div className="bg-muted/30 rounded-lg p-6">
        <h4 className="font-medium mb-4 flex items-center space-x-2">
          <Icon name="MousePointer" size={16} className="text-accent" />
          <span>Interactive Elements</span>
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mx-auto mb-2 hover:scale-110 transition-transform duration-300 cursor-pointer"></div>
            <p className="text-sm text-text-secondary">Hover to Scale</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-teal-600 rounded-lg mx-auto mb-2 hover:rotate-45 transition-transform duration-500 cursor-pointer"></div>
            <p className="text-sm text-text-secondary">Hover to Rotate</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-rose-600 rounded-full mx-auto mb-2 animate-pulse cursor-pointer"></div>
            <p className="text-sm text-text-secondary">Pulsing Animation</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSSPlayground;